import * as R from 'ramda'
import * as actions from './actionTypes.js'
import { LOG_OUT } from 'ducks/auth.js'

const STATUS = {
  ok: 'ok',
  patching: 'patching',
  fetching: 'fetching',
  deleting: 'deleting',
  deleted: 'deleted',
  error: 'error',
}

// Template state for model
const baseModelState = { items: {}, meta: { selected: {} } }

// objFromKeys :: (String -> a) -> [String] -> {String: a}
const objFromKeys = R.curry((fn, keys) => R.zipObj(keys, R.map(fn, keys)))

// initialize state by passing an array of model names, representings sub
// slices
// [str] -> {str: {k:v}}
export const initialOrmState = objFromKeys(R.always(baseModelState))

// makeReducer :: (state, {type: state=>state}) -> (state, {type}) -> state
const makeReducer = (initialState = {}, actionMapping = {}) => action => (
  state = initialState,
) => {
  const handler = actionMapping[action.type]
  return handler ? handler(action)(state) : state
}

// reducer for meta
const metaReducer = makeReducer(baseModelState.meta, {
  [actions.TOGGLE_SELECT]: ({ payload }) =>
    R.over(R.lensPath(['selected', payload]), R.not),
  [actions.SET_SELECTION]: ({ payload }) =>
    R.set(R.lensProp('selected'), payload),
  [actions.RESET_META]: () => R.always(baseModelState.meta),
})

// helper functions
const statusLens = R.lensProp('__status')
const dirtyLens = R.lensProp('__dirty')
const errorLens = R.lensProp('__error')

const setStatus = R.set(statusLens)

export const updateItem = data => R.over(dirtyLens, R.mergeLeft(data))

// remove dirty props that are identical to the pristine
export const cleanDirty = item => {
  const stillDirty = (value, key) => R.complement(R.propEq)(key, value, item)
  return R.over(
    dirtyLens,
    R.pipe(
      R.defaultTo({}),
      R.pickBy(stillDirty),
    ),
    item,
  )
}

// trims whitespace from all dirty string model instance properties
const trimWhiteSpace = R.over(dirtyLens, R.map(R.when(R.is(String), R.trim)))

// set error value, typically a validation error from the rest api
const setError = payload =>
  R.pipe(
    R.set(errorLens, payload.error),
    setStatus(payload.error ? STATUS.error : STATUS.ok),
  )

// merge action payload (from patch/get) into store model instance
const mergePayload = payload =>
  R.pipe(
    R.mergeLeft(R.dissoc('__meta', payload)),
    cleanDirty,
    setError(payload),
  )

// reducer for single model entry
const getItemReducer = ({ type, payload }) =>
  ({
    [actions.CHANGE]: R.pipe(
      updateItem(payload),
      cleanDirty,
    ),
    [actions.RESET]: R.pipe(
      R.set(dirtyLens, payload),
      setError({}),
    ),
    [actions.FETCH_START]: setStatus(STATUS.fetching),
    [actions.DELETE_START]: setStatus(STATUS.deleting),
    [actions.PATCH_START]: R.pipe(
      trimWhiteSpace,
      setStatus(STATUS.patching),
    ),
    [actions.POST_START]: R.pipe(
      trimWhiteSpace,
      setStatus(STATUS.patching),
    ),
    [actions.FETCH_SUCCESS]: mergePayload(payload),
    [actions.POST_SUCCESS]: mergePayload(payload),
    [actions.PATCH_SUCCESS]: mergePayload(payload),
    [actions.PATCH_ERROR]: setError(payload),
    [actions.POST_ERROR]: setError(payload),
    [actions.DELETE_SUCCESS]: R.always(setStatus(STATUS.deleted, {})),
  }[type] || R.identity)

// base reducer for orm
export const reducer = (state = {}, action = {}) => {
  const { meta: { model, pk } = {}, payload } = action
  if (action.type === LOG_OUT)
    // clear orm cache on log out
    return {}

  if (action.type === actions.PENDING_REQUEST) {
    return R.mergeDeepRight(state, action.payload)
  }

  if (!model)
    // all valid orm actions have `meta.model` property
    // ignore actions without `meta.model`
    return state
  if (pk) {
    // if there's a `pk` property, the action is for a single item
    return R.pipe(
      R.over(R.lensPath([model, 'items', `${pk}`]), (item = {}) =>
        getItemReducer(action)(item),
      ),
      action.type === actions.DELETE_SUCCESS
        ? R.over(R.lensPath([model, 'order']), R.without([pk]))
        : R.identity,
    )(state)
  }
  const results = R.prop('results', payload)
  if (results) {
    const count = R.prop('count', payload)
    const order = R.pipe(
      R.pluck('id'),
      R.map(String),
    )(results)
    const path = R.lensPath([model])

    // TODO: this is hacky. Should be done in a more elegant way
    // Received multiple results from the api
    // create actions and recurse for each item in results
    const slice = R.pipe(
      R.map(result => ({
        type: action.type,
        meta: { model, pk: result.id },
        payload: result,
      })),
      R.reduce(reducer, {
        [model]: { count: null, items: {}, meta: { selected: {} } },
      }),
    )(results)

    return R.over(path, () => ({ ...slice[model], count, order }), state)
  }
  // other actions are passed to the meta reducer for that model slice

  return R.over(R.lensPath([model, 'meta']), metaReducer(action), state)
}
